import ArticlePage from "@/integrations/strapi-cms/ArticlePage";
import Blog from "@/integrations/strapi-cms/Blog";
import Contact from "@/pages/Contact/Contact";
import LegalCenter from "@/pages/LegalCenterPage/LegalCenter";
import Faq from "@/pages/LegalCenterPage/components/Faq";
import Privacy from "@/pages/LegalCenterPage/components/Privacy";
import Tos from "@/pages/LegalCenterPage/components/Tos";
import { useTranslation } from "@repo/shared/i18n";
import { Suspense, lazy } from "react";
import { Navigate, RouteObject, useLocation, useParams } from "react-router";

import { RouteErrorBoundary } from "./components/RouteErrorBoundary";
import { RootLayout } from "./layouts/RootLayout";
import { AppLandingPage } from "./pages/AppLanding/AppLandingPage";
import { BecomePartner } from "./pages/BecomePartner/BecomePartner";
import CheapestItemScorePage from "./pages/CheapestItemScore/CheapestItemScorePage";
import { ClubImportTutorial } from "./pages/ClubImportTutorial/ClubImportTutorial";
import { ClubImportGuide } from "./pages/ClubImportTutorial/MobileComponents/ClubImportGuide";
import { SetupExtensionSection } from "./pages/ClubImportTutorial/MobileComponents/SetupExtensionSection";
import ComparePage from "./pages/Compare/ComparePage";
import CompareMobilePage from "./pages/Compare/mobile/CompareMobilePage";
import { CreatorLandingPage } from "./pages/CreatorLandingPage/CreatorLandingPage";
import { DetailedPlayer } from "./pages/DetailedPlayer/DetailedPlayer";
import BotPrivacy from "./pages/DiscordBot/Privacy";
import BotTos from "./pages/DiscordBot/Tos";
import EmailUnsubscribe from "./pages/EmailUnsubscribe/EmailUnsubscribe";
import { EvolutionStatesPage } from "./pages/EvolutionsHub/EvolutionStatesPage/EvolutionStatesPage";
import { EvolutionBuilderPlayerPicker } from "./pages/EvolutionsHub/EvolutionsBuilder/EvolutionBuilderPlayerPicker";
import { EvolutionsBuilder } from "./pages/EvolutionsHub/EvolutionsBuilder/EvolutionsBuilder";
import { EvolutionsLayout } from "./pages/EvolutionsHub/EvolutionsLayout";
import { DetailedEvoPage } from "./pages/EvolutionsHub/EvolutionsOverviewPage/DetailedEvoPage/DetailedEvoPage";
import { EvolutionsOverviewPage } from "./pages/EvolutionsHub/EvolutionsOverviewPage/EvolutionsOverviewPage";
import { EvolutionsPage } from "./pages/EvolutionsHub/EvolutionsPage/EvolutionsPage";
import { EvolvedPlayersPage } from "./pages/EvolutionsHub/MyEvolvedPlayersPage/EvolvedPlayersPage";
import { MyEvolvedPlayersLayout } from "./pages/EvolutionsHub/MyEvolvedPlayersPage/MyEvolvedPlayers";
import { SavedEvoPlayersPage } from "./pages/EvolutionsHub/MyEvolvedPlayersPage/SavedEvoPlayersPage";
import { FeaturebaseSso } from "./pages/FeaturebaseSso/FeaturebaseSso";
import FrontPage from "./pages/FrontPage/FrontPage";
import GalleryFirstOwnersPage from "./pages/Gallery/GalleryFirstOwnersPage";
import GalleryLevelsPage from "./pages/Gallery/GalleryLevelsPage";
import GalleryPage from "./pages/Gallery/GalleryPage";
import GallerySetPage from "./pages/Gallery/GallerySetPage";
import ImportTroubleshoot from "./pages/ImportTroubleshoot/ImportTroubleshoot";
import { MyPlayersLandingPage } from "./pages/LandingPages/Features/MyPlayersLandingPage";
import { SBCSolverLandingPage } from "./pages/LandingPages/Features/SBCSolverLandingPage";
import { SquadBuilderLandingPage } from "./pages/LandingPages/Features/SquadBuilderLandingPage";
import { GetUltimateLandingPage } from "./pages/LandingPages/GetUltimateLandingPage";
import MetaRatingLandingPage from "./pages/MetaRatingLandingPage/MetaRatingLandingPage";
import { MyClubFavorites } from "./pages/MyClub/Favorites/MyClubFavorites";
import { MyClub } from "./pages/MyClub/MyClub";
import { MyClubPlayers } from "./pages/MyClub/Players/MyClubPlayers";
import { MyClubRecommendations } from "./pages/MyClub/Recommendations/MyClubRecommendations";
import { MyClubSbcFodderBreakdown } from "./pages/MyClub/SBCFodderBreakdown/MyClubSbcFodderBreakdown";
import ObjectiveGroupPage from "./pages/Objectives/ObjectiveGroupPage";
import ObjectivePlanPage from "./pages/Objectives/ObjectivePlanPage";
import ObjectivesPage from "./pages/Objectives/ObjectivesPage";
import { ObjectivesAccessGate } from "./pages/ObjectivesLanding/ObjectivesAccessGate";
import SubscriberOnboarding from "./pages/Onboarding/SubscriberOnboarding";
import PaymentSuccess from "./pages/Payments/PaymentSuccess";
import { PlayStylesLab } from "./pages/PlayStylesLab/PlayStylesLab";
import { PlayStylesLabPlayerPicker } from "./pages/PlayStylesLab/PlayStylesLabPlayerPicker";
import { PlayerImages } from "./pages/PlayerImages/PlayerImages";
import { playerImagesLoader } from "./pages/PlayerImages/playerImagesLoader";
import PlayersPage from "./pages/Players/PlayersPage";
import Profile from "./pages/Profile/Profile";
import SbcChallengePage from "./pages/Sbcs/SbcChallengePage";
import SbcSetPage from "./pages/Sbcs/SbcSetPage/SbcSetPage";
import SbcSetsPage from "./pages/Sbcs/SbcSetsPage";
import SquadBuilder from "./pages/SquadBuilder/SquadBuilder";
import Subscription from "./pages/Subscription/Subscription";
import { SweepStakesTos } from "./pages/SweepStakesTos/SweepStakesTos";
import { TacticsPage } from "./pages/Tactics/TacticsPage";
import WhoAreWe from "./pages/WhoAreWe/WhoAreWe";
import { fetchSbcsSets } from "./redux/sbcs/sbcSetsSlice";
import {
  fetchSbc,
  fetchSbcSet,
  fetchSbcs,
  getSbcSelector,
} from "./redux/sbcs/sbcsSlice";
import store from "./redux/store";
import { canUseClubImportSelector } from "./redux/user/userSlice";

const detailedPlayerRoutes: RouteObject[] = [
  { path: ":name/:resourceId", element: <DetailedPlayer /> },
  { path: ":name/:version/:resourceId", element: <DetailedPlayer /> },
  {
    path: ":resourceId/images",
    element: <PlayerImages />,
    loader: playerImagesLoader,
  },
  {
    path: ":name/:resourceId/images",
    element: <PlayerImages />,
    loader: playerImagesLoader,
  },
  {
    path: ":name/:version/:resourceId/images",
    element: <PlayerImages />,
    loader: playerImagesLoader,
  },
];

// Main routes configuration
/*
  Showcase routes — localhost/staging only.

  Gated on the build-time constant VITE_APP_SHOW_COMPONENTS (set to "true" locally
  and on staging, unset/"false" in production). Because the condition folds to a
  literal at build time, the `import()`s inside the dead branch are dropped in prod —
  the chunks (and every globbed *.examples.tsx file) are never emitted.
*/
let devOnlyRoutes: RouteObject[] = [];
if (import.meta.env.VITE_APP_SHOW_COMPONENTS === "true") {
  const Components = lazy(() => import("./pages/Components/Components"));
  const DesignSystem = lazy(() => import("./pages/DesignSystem/DesignSystem"));
  const DialogLab = lazy(() => import("./pages/Components/DialogLab"));
  devOnlyRoutes = [
    {
      path: "components",
      element: (
        <Suspense fallback={null}>
          <Components />
        </Suspense>
      ),
    },
    {
      path: "dialogs",
      element: (
        <Suspense fallback={null}>
          <DialogLab />
        </Suspense>
      ),
    },
    {
      path: "design-system",
      element: (
        <Suspense fallback={null}>
          <DesignSystem />
        </Suspense>
      ),
    },
  ];
}

// A component rather than inline JSX: `routes` is built at module load, before initI18n.
const NotFound = () => {
  const { t } = useTranslation();
  return (
    <main className="z-10 mt-8 text-2xl text-center text-white">
      {t("pageNotFound")}
    </main>
  );
};

/** A moved URL tree: the rest of the path and the query (filters, sort) kept. */
const LegacyRedirect = ({ to }: { to: string }) => {
  const { "*": rest } = useParams();
  const { search } = useLocation();
  return <Navigate to={(rest ? `${to}/${rest}` : to) + search} replace />;
};

export const routes: RouteObject[] = [
  {
    path: "/",
    element: <RootLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      // Home
      {
        index: true,
        element: <FrontPage />,
        loader: async () => {
          // The front page only needs the sbc sets when the club-import
          // selector is unavailable.
          if (!canUseClubImportSelector(store.getState())) {
            store.dispatch(fetchSbcsSets());
          }
          return null;
        },
      },
      {
        path: "sbc",
        element: <SbcSetsPage />,
        loader: async () => {
          store.dispatch(fetchSbcsSets());
          return null;
        },
      },
      // Waitlist-only: without granted access these all show the landing page.
      {
        path: "objectives",
        element: (
          <ObjectivesAccessGate>
            <ObjectivesPage />
          </ObjectivesAccessGate>
        ),
      },
      {
        path: "objectives/:name/:groupId",
        element: (
          <ObjectivesAccessGate>
            <ObjectiveGroupPage />
          </ObjectivesAccessGate>
        ),
      },
      {
        path: "objective-plan/:bundleId",
        element: (
          <ObjectivesAccessGate>
            <ObjectivePlanPage />
          </ObjectivesAccessGate>
        ),
      },
      // Auth routes
      { path: "profile", element: <Profile /> },
      { path: "login", element: <Navigate to="/?login=1" replace /> },
      { path: "sso/featurebase", element: <FeaturebaseSso /> },

      // Squad Builder
      {
        path: "squad-builder",
        element: <SquadBuilder />,
        children: detailedPlayerRoutes,
      },

      // Players
      {
        path: "players",
        element: <PlayersPage />,
        children: detailedPlayerRoutes,
      },

      // Tactics
      { path: "tactics", element: <TacticsPage /> },

      // Compare players
      { path: "compare", element: <ComparePage /> },
      { path: "compare/mobile", element: <CompareMobilePage /> },

      // PlayStyles Lab
      {
        path: "playstyles-lab",
        element: <PlayStylesLabPlayerPicker />,
        children: [
          { path: ":name/:resourceId", element: <PlayStylesLab /> },
          { path: ":name/:version/:resourceId", element: <PlayStylesLab /> },
          {
            path: "details/:name/:resourceId",
            element: <DetailedPlayer />,
          },
          {
            path: "details/:name/:version/:resourceId",
            element: <DetailedPlayer />,
          },
        ],
      },

      // Evolutions
      {
        path: "evolutions",
        element: <EvolutionsLayout />,
        children: [
          { index: true, element: <EvolutionsOverviewPage /> },
          {
            path: "builder",
            element: <EvolutionBuilderPlayerPicker />,
            children: [
              {
                path: ":name/:resourceId",
                element: <EvolutionsBuilder />,
              },
              {
                path: ":name/:version/:resourceId",
                element: <EvolutionsBuilder />,
              },
              {
                path: "details/:name/:resourceId",
                element: <DetailedPlayer />,
              },
              {
                path: "details/:name/:version/:resourceId",
                element: <DetailedPlayer />,
              },
            ],
          },
          {
            path: "saved",
            element: <DetailedPlayer />,
            children: detailedPlayerRoutes,
          },
          {
            path: "players",
            element: <EvolutionsPage />,
            children: detailedPlayerRoutes,
          },
          {
            path: "my-evolutions",
            element: <MyEvolvedPlayersLayout />,
            children: [
              { index: true, element: <Navigate to="players" replace /> },
              {
                path: "players",
                element: <EvolvedPlayersPage />,
                children: detailedPlayerRoutes,
              },
              {
                path: "saved",
                element: <SavedEvoPlayersPage />,
              },
              { path: "planned", element: <Navigate to="../saved" replace /> },
            ],
          },
          { path: "overview", element: <Navigate to=".." replace /> },
          {
            path: "overview/:name/:evoId",
            element: <DetailedEvoPage />,
          },
          {
            // "Tracker" is what players search for; the page itself talks
            // about evolution states.
            path: "tracker",
            element: <EvolutionStatesPage />,
          },
          { path: "states", element: <Navigate to="../tracker" replace /> },
        ],
      },
      // SBC routes
      {
        path: "sbc/:name/:setId",
        element: <SbcSetPage />,
        loader: async ({ params }) => {
          const setId = Number(params.setId);
          store.dispatch(fetchSbcSet({ setId }));
          store.dispatch(fetchSbcs({ setId }));
          return null;
        },
      },
      // Early access signup for the iPhone and Android apps.
      { path: "app", element: <AppLandingPage /> },
      { path: "fut-gallery", element: <GalleryPage /> },
      { path: "fut-gallery/levels", element: <GalleryLevelsPage /> },
      {
        path: "fut-gallery/update-first-owners",
        element: <GalleryFirstOwnersPage />,
      },
      {
        path: "fut-gallery/:categorySlug/:setSlug",
        element: <GallerySetPage />,
      },
      // Moved URLs; shared links and older extension builds keep working.
      { path: "gallery/*", element: <LegacyRedirect to="/fut-gallery" /> },
      { path: "sbcs", element: <LegacyRedirect to="/sbc" /> },
      { path: "sbc-solution/*", element: <LegacyRedirect to="/sbc" /> },
      { path: "streamline-sbc/*", element: <LegacyRedirect to="/sbc" /> },
      {
        // Normal and Streamline challenges; the set's kind picks the page.
        path: "sbc/:setName/:setId/:sbcName/:sbcId",
        element: <SbcChallengePage />,
        loader: async ({ params }) => {
          const sbcId = Number(params.sbcId);
          const setId = Number(params.setId);
          // From the set page the challenge is already in the store; fetching
          // it again would replace the set's challenge list with this one.
          if (!getSbcSelector(sbcId, setId)(store.getState()))
            store.dispatch(fetchSbc({ sbcId, setId }));
          store.dispatch(fetchSbcSet({ setId }));
          return null;
        },
      },

      // Discord bot routes
      { path: "eafcassistant/tos", element: <BotTos /> },
      { path: "eafcassistant/privacy", element: <BotPrivacy /> },

      // Info pages
      { path: "who-are-we", element: <WhoAreWe /> },
      { path: "contact", element: <Contact /> },

      { path: "essentials", element: <SubscriberOnboarding /> },

      // Legal routes
      { path: "sweepstakes-tos", element: <SweepStakesTos /> },
      { path: "tos", element: <Navigate to="/legal/tos" replace /> },
      { path: "privacy", element: <Navigate to="/legal/privacy" replace /> },
      {
        path: "legal",
        element: <LegalCenter />,
        children: [
          { index: true, element: <Privacy /> },
          { path: "faq", element: <Faq /> },
          { path: "privacy", element: <Privacy /> },
          { path: "tos", element: <Tos /> },
        ],
      },

      // Payment
      { path: "payment-success", element: <PaymentSuccess /> },
      { path: "subscription", element: <Subscription /> },

      // Become a Partner
      { path: "become-a-partner", element: <BecomePartner /> },
      {
        path: "creator-academy",
        element: <Navigate to="/become-a-partner" replace />,
      },

      // Import tutorials
      { path: "import-mobile", element: <ClubImportTutorial /> },
      {
        path: "club-import-tutorial",
        element: <ClubImportTutorial />,
        children: [
          {
            path: "mobile-setup-extension",
            element: <SetupExtensionSection />,
          },
          {
            path: "mobile-club-import-guide",
            element: <ClubImportGuide />,
          },
        ],
      },
      { path: "import-troubleshoot", element: <ImportTroubleshoot /> },

      // Landing pages
      { path: "get-my-players", element: <MyPlayersLandingPage /> },
      { path: "get-sbc-solver", element: <SBCSolverLandingPage /> },
      { path: "get-ai-squad-builder", element: <SquadBuilderLandingPage /> },
      { path: "meta-rating", element: <MetaRatingLandingPage /> },
      { path: "cheapest-item-score", element: <CheapestItemScorePage /> },
      { path: "creator", element: <CreatorLandingPage /> },
      { path: "get-ultimate", element: <GetUltimateLandingPage /> },

      // My Club
      {
        path: "my-club",
        element: <MyClub />,
        children: [
          { path: "players", element: <MyClubPlayers /> },
          { path: "recommendations", element: <MyClubRecommendations /> },
          {
            path: "favorite-club",
            element: <MyClubFavorites type="club" />,
          },
          {
            path: "favorite-country",
            element: <MyClubFavorites type="country" />,
          },
          {
            path: "sbc-fodder-breakdown",
            element: <MyClubSbcFodderBreakdown />,
          },
          ...detailedPlayerRoutes,
        ],
      },

      // News/Blog
      { path: "news", element: <Blog /> },
      { path: "news/:slug", element: <ArticlePage /> },

      // Email
      { path: "email-unsubscribe", element: <EmailUnsubscribe /> },

      // Showcase routes: /components + /design-system (localhost/staging only)
      ...devOnlyRoutes,

      // 404 catch-all
      {
        path: "*",
        element: <NotFound />,
      },
    ],
  },
];
